import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError, loggerLink } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { registerServiceWorker, initializeOnlineStatus } from "./registerSW";
import { initFrontendErrorReporting } from "@/lib/errorReporting";
import { initializeOfflineSync, syncPendingDailyLogs } from "@/lib/offlineSync";
import { getOutboxCounts } from "@/lib/offlineOutbox";

console.info("BUILD_TAG", new Date().toISOString().slice(0,10));

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  const loginUrl = getLoginUrl();
  if (loginUrl) {
    window.location.href = loginUrl;
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);

    if (error instanceof TRPCClientError && error.data?.code && (error.data.code === 'UNAUTHORIZED' || error.data.code === 'FORBIDDEN')) {
      console.error("[Expected RBAC Error - suppressed]", error);
      return;
    }

    // Report tRPC query errors to system logs
    if (error && typeof error === 'object' && 'message' in error) {
      import("@/lib/errorReporting").then(({ reportError }) => {
        reportError(
          "error",
          `TRPC Query Error: ${error.message || 'Unknown error'}`,
          window.location.pathname,
          error,
          { trpcError: true, isQuery: true }
        );
      });
    }

    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);

    if (error instanceof TRPCClientError && error.data?.code && (error.data.code === 'UNAUTHORIZED' || error.data.code === 'FORBIDDEN')) {
      console.error("[Expected RBAC Error - suppressed]", error);
      return;
    }

    // Report tRPC mutation errors to system logs
    if (error && typeof error === 'object' && 'message' in error) {
      import("@/lib/errorReporting").then(({ reportError }) => {
        reportError(
          "error",
          `TRPC Mutation Error: ${error.message || 'Unknown error'}`,
          window.location.pathname,
          error,
          { trpcError: true, isMutation: true }
        );
      });
    }

    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Initialize frontend error reporting after React setup
initFrontendErrorReporting();

// Register service worker for PWA
registerServiceWorker();
initializeOnlineStatus();

// Initialize offline sync engine
initializeOfflineSync();

// Set up periodic sync (every 5 minutes when online)
setInterval(async () => {
  if (navigator.onLine) {
    try {
      const counts = await getOutboxCounts();
      if (counts.pending > 0) {
        await syncPendingDailyLogs();
        console.log('[Offline] Periodic sync completed');
      }
    } catch (error) {
      console.warn('[Offline] Periodic sync failed:', error);
    }
  }
}, 5 * 60 * 1000); // 5 minutes

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);